/**
 * Benachrichtigungs-Einstellungen des angemeldeten Agents (Spec 8.5).
 *
 * Die Matrix ist klein genug fuer Kaestchen: drei Ereignisse mal zwei
 * zustellbare Kanaele. `push` fehlt bewusst — es gibt keinen Zusteller
 * (`TODO.md`), und ein Schalter ohne Wirkung ist schlimmer als keiner.
 *
 * **Wichtig fuer das Verstaendnis:** wer nie etwas gespeichert hat, bekommt
 * E-Mails zu allen drei Ereignissen (`DEFAULT_NOTIFICATION_PREF`). Das steht
 * so auch im Hinweistext — sonst wundert sich jemand ueber Post, die er nie
 * bestellt hat.
 */
import {
  DELIVERABLE_NOTIFICATION_CHANNELS,
  NOTIFICATION_EVENTS,
  type NotificationChannel,
  type NotificationEvent,
  type NotificationPref,
} from '@tickethub/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

type NotificationPrefsFormProps = {
  prefs: NotificationPref[];
  /** `true`, solange nichts gespeichert wurde — dann gilt der Default. */
  usingDefault: boolean;
  onSave: (prefs: { channel: NotificationChannel; events: NotificationEvent[] }[]) => Promise<void>;
};

export function NotificationPrefsForm({ prefs, usingDefault, onSave }: NotificationPrefsFormProps) {
  const { t } = useTranslation();

  const [selection, setSelection] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const channel of DELIVERABLE_NOTIFICATION_CHANNELS) {
      const stored = prefs.find((pref) => pref.channel === channel);
      for (const event of NOTIFICATION_EVENTS) {
        initial[`${channel}/${event}`] = usingDefault
          ? channel === 'email'
          : (stored?.events.includes(event) ?? false);
      }
    }
    return initial;
  });
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await onSave(
        DELIVERABLE_NOTIFICATION_CHANNELS.map((channel) => ({
          channel,
          events: NOTIFICATION_EVENTS.filter((event) => selection[`${channel}/${event}`] === true),
        })),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      {usingDefault ? (
        <p className="text-sm text-muted-foreground">{t('notification.default_hint')}</p>
      ) : null}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">{t('notification.event')}</th>
              {DELIVERABLE_NOTIFICATION_CHANNELS.map((channel) => (
                <th key={channel} className="px-3 py-2 font-medium">
                  {t(`notification.channel_${channel}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {NOTIFICATION_EVENTS.map((event) => (
              <tr key={event} className="border-t">
                <td className="px-3 py-2">{t(`notification.event_${event}`)}</td>
                {DELIVERABLE_NOTIFICATION_CHANNELS.map((channel) => (
                  <td key={channel} className="px-3 py-2">
                    <label className="flex items-center gap-2">
                      <span className="sr-only">
                        {t(`notification.event_${event}`)} — {t(`notification.channel_${channel}`)}
                      </span>
                      <input
                        type="checkbox"
                        checked={selection[`${channel}/${event}`] === true}
                        onChange={(change) =>
                          setSelection((current) => ({
                            ...current,
                            [`${channel}/${event}`]: change.target.checked,
                          }))
                        }
                      />
                    </label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">{t('notification.slack_hint')}</p>

      <Button type="submit" size="sm" disabled={busy}>
        {busy ? t('common.saving') : t('common.save')}
      </Button>
    </form>
  );
}
